// Route table — single source of truth for SPA navigation.
//
// All routes nest under the AppShell layout (sidebar + content
// outlet). Components are wrapped in `lazy()` so Vite emits one
// chunk per top-level view; navigation downloads on demand.
//
// Phase 6 ports the legacy Leptos views one at a time. Migration
// status:
//
//   ✓ Welcome              — Phase 1 placeholder, retained
//   ✓ Settings / Me        — fully migrated (settings_me.rs)
//   ⨯ everything else      — stub via _NotMigrated.svelte, route
//                            entry preserved so existing URLs and
//                            sidebar links don't 404
//
// Adding a new migrated view: drop the .svelte file in src/views/,
// then replace the relevant `lazyStub(...)` below with a proper
// `lazy(() => import(...))`.

import type { Route } from "./router";
import { lazy } from "./router";

/** Stub a route that hasn't been migrated yet. Renders the
 *  `_NotMigrated` view, which surfaces `route.pathname` so a bug
 *  report screenshots itself. The legacy module hint here is for
 *  greppability when picking the next view to port. */
function lazyStub(_legacyView: string): Route["component"] {
  return lazy(() => import("./views/_NotMigrated.svelte"));
}

export const routes: Route[] = [
  {
    path: "/",
    component: lazy(() => import("./views/_AppShell.svelte")),
    children: [
      // Default landing — chats list. Welcome page still served at
      // /welcome (legacy redirect target).
      { path: "", component: lazy(() => import("./views/Chats.svelte")) },
      { path: "welcome", component: lazy(() => import("./views/Welcome.svelte")) },

      // Bare /chats is the mobile chat-history screen (the Assistent
      // tab's drill-down root); on desktop it redirects to / where
      // the sidebar owns history.
      { path: "chats",              component: lazy(() => import("./views/ChatHistory.svelte")) },
      { path: "chats/:id",          component: lazy(() => import("./views/ChatDetail.svelte")) },
      { path: "agents",             component: lazy(() => import("./views/Agents.svelte")) },
      {
        path: "agents/:id",
        component: lazy(() => import("./views/AgentDetail.svelte")),
        children: [
          { path: "",            component: lazy(() => import("./views/agent_detail/Overview.svelte")) },
          { path: "runs",        component: lazy(() => import("./views/agent_detail/Runs.svelte")) },
          { path: "behavior",    component: lazy(() => import("./views/agent_detail/Behavior.svelte")) },
          { path: "automations", component: lazy(() => import("./views/agent_detail/Automations.svelte")) },
          { path: "memory",      component: lazy(() => import("./views/agent_detail/Memory.svelte")) },
          { path: "tech",        component: lazy(() => import("./views/agent_detail/Tech.svelte")) },
        ],
      },
      { path: "cases",                    component: lazy(() => import("./views/Cases.svelte")) },
      { path: "cases/:id",                component: lazy(() => import("./views/CaseDetail.svelte")) },
      // Automations / Automationen — tenant overview + create/detail/edit.
      // Detail + config are top-level (not nested under agents/:id) so the
      // route params stay unambiguous; the agent is carried as ?agent=<id>.
      { path: "automations",          component: lazy(() => import("./views/Automations.svelte")) },
      { path: "automations/new",      component: lazy(() => import("./views/AutomationConfig.svelte")) },
      { path: "automations/:id",      component: lazy(() => import("./views/AutomationDetail.svelte")) },
      { path: "automations/:id/edit", component: lazy(() => import("./views/AutomationConfig.svelte")) },
      { path: "activity",                 component: lazy(() => import("./views/Activity.svelte")) },
      // Freigaben inbox — parked approvals the caller may decide.
      { path: "approvals",                component: lazy(() => import("./views/Approvals.svelte")) },
      // Content index — HIDDEN, not removed (2026-09-09). The feature
      // runs and ingests; its vector half has never worked because the
      // embedding service is down, and its admin surface reports state
      // for something nobody can act on yet. The views, the API and the
      // indexer are all untouched: reverting this commit brings the
      // page back exactly. See the sidebar and status bar for the other
      // two entry points removed with it.
      { path: "memory",                   component: lazy(() => import("./views/Memory.svelte")) },
      { path: "memory/stores/:id",        component: lazy(() => import("./views/MemoryStoreDetail.svelte")) },

      // Settings — Settings/Me is the first migrated. The rest stub
      // out until their turn in the queue.
      // Bare /settings is the mobile drill-down landing (the sidebar's
      // section list as a screen); on desktop it redirects to /settings/me.
      { path: "settings",                 component: lazy(() => import("./views/settings/Index.svelte")) },
      { path: "settings/me",              component: lazy(() => import("./views/settings/Me.svelte")) },
      { path: "settings/general",         component: lazy(() => import("./views/settings/Settings.svelte")) },
      // The same page on sample data, for looking at it without a backend.
      // Development only: a shipped Hub has no page that shows made-up
      // settings, and the bundler drops the sample data with the route.
      ...(import.meta.env.DEV
        ? [{ path: "settings/preview",       component: lazy(() => import("./views/settings/Preview.svelte")) }]
        : []),
      { path: "settings/tenants",         component: lazy(() => import("./views/settings/Tenants.svelte")) },
      { path: "settings/apps",            component: lazy(() => import("./views/settings/Apps.svelte")) },
      { path: "settings/apps/:installation_id", component: lazy(() => import("./views/settings/AppDetail.svelte")) },
      { path: "settings/audit",           component: lazy(() => import("./views/settings/Audit.svelte")) },
      { path: "settings/groups",          component: lazy(() => import("./views/settings/Groups.svelte")) },
      { path: "settings/roles",           component: lazy(() => import("./views/settings/Roles.svelte")) },
      { path: "settings/users",           component: lazy(() => import("./views/settings/UsersAgents.svelte")) },
      { path: "settings/access-requests", component: lazy(() => import("./views/settings/AccessRequests.svelte")) },
      { path: "settings/resource-activity", component: lazy(() => import("./views/settings/ResourceActivity.svelte")) },
      { path: "settings/system",          component: lazy(() => import("./views/settings/System.svelte")) },
      // Legacy aliases — old links kept working.
      { path: "settings/tenant-memory",   component: lazyStub("settings_tenant_memory") },
      { path: "settings/memory-stores",   component: lazyStub("settings_memory_stores") },
    ],
  },

  // Unauthenticated login page — outside the shell (no sidebar, no
  // auth bootstrap/redirect). The shell's 401 effect lands here;
  // the page renders whatever /auth/methods reports (LDAP form,
  // SSO button, or auto-forwards for SSO-only deployments).
  { path: "/login", component: lazy(() => import("./views/Login.svelte")) },

  // Post-login workspace picker for multi-tenant users — outside the
  // shell (no chrome) like /login. The shell routes here when an
  // authenticated identity belongs to more than one workspace; the page
  // reuses the tenant-switch endpoints. Single-tenant users never see it.
  {
    path: "/choose-workspace",
    component: lazy(() => import("./views/ChooseWorkspace.svelte")),
  },

  // Catch-all 404 — only reached when neither the shell nor any
  // child matches. Stays outside the shell so a typo URL doesn't
  // render the sidebar.
  { path: "*", component: lazy(() => import("./views/NotFound.svelte")) },
];
